// Client-side verbatim slicing of the rounds working document (beekeeper-agent-chat, `bpp-colony-
// document`; `client-colony` "In `GameMode::Rounds` the source rendered SHALL be that type's
// verbatim slice of the colony document"). A DISPLAY concern only — the server's
// `waggle_bpp::document::ColonyDocument::slice`/`hand_edit_slice` remain the authority for what a
// `HandEdit` actually rewrites; this exists so the Code tab can show and pre-fill a draft from the
// document's own bytes (comments included) without waiting on a round-trip, and so it can degrade
// gracefully when the document does not parse at all (no per-type slice is extractable then).

/** Matches a `type <name> ...` header at the start of a line — never `birth_type` (a different
 * leading token, so no false match), and case-sensitive/verbatim like the grammar itself. */
const TYPE_HEADER_RE = /^type\s+(\S+)/gm;

/** Extracts `typeName`'s verbatim slice from `document`: from its `type <name>` header line to the
 * byte immediately before the next `type` header, or to the end of the document for the last
 * block — mirroring `ColonyDocument::slice`'s semantics closely enough for rendering. `null` when
 * `typeName` has no header in `document` at all. */
export function sliceType(document: string, typeName: string): string | null {
  const range = typeBlockRange(document, typeName);
  return range === null ? null : document.slice(range.start, range.end);
}

/**
 * Puts an edited slice back where {@link sliceType} took it from — the whole document the client
 * saves, since `POST /document` takes the document and the sheet's editor edits one block of it.
 *
 * Returns the document unchanged when `typeName` has no header, which is the same "nothing
 * extractable to edit" case {@link sliceType} reports with `null`; the sheet withholds its editor
 * there, so this is a guard rather than a path.
 */
export function replaceTypeSlice(document: string, typeName: string, slice: string): string {
  const range = typeBlockRange(document, typeName);
  return range === null
    ? document
    : document.slice(0, range.start) + slice + document.slice(range.end);
}

/** A type block's byte range: its `type <name>` header to the byte before the next header, or to
 * the end of the document for the last block. */
function typeBlockRange(document: string, typeName: string): { start: number; end: number } | null {
  const headers: { name: string; index: number }[] = [];
  for (const match of document.matchAll(TYPE_HEADER_RE)) {
    headers.push({ name: match[1]!, index: match.index });
  }
  const at = headers.findIndex((h) => h.name === typeName);
  if (at === -1) {
    return null;
  }
  return {
    start: headers[at]!.index,
    end: at + 1 < headers.length ? headers[at + 1]!.index : document.length,
  };
}

/**
 * Which type's block a **1-indexed document line** falls in, or `null` when it falls in none.
 *
 * The inverse of {@link sliceType}, and it exists for one specific obligation: the blocked-Run
 * popup must "offer to open the first offending location" (`client-sandbox-screen`, design
 * **D12**). The wire's `DocumentDiagnostic` carries only a line and a column — the compiler reports
 * against the whole document, which is the unit it compiles — so something has to attribute that
 * line to a type before a control can open a slice. This is that something, and it lives here
 * because the type-header scan it needs is already here.
 *
 * `null` is a real answer, not a failure: a line above the first `type` header belongs to the
 * document's preamble (the `birth_type` line, leading comments), and an "open" control that cannot
 * say *where* would send the player somewhere invented. The caller offers no control for `null`.
 */
export function typeAtLine(document: string, line: number): string | null {
  if (!Number.isFinite(line) || line < 1) {
    return null;
  }
  const lines = document.split('\n');
  if (line > lines.length) {
    return null;
  }
  // Walk back from the target line to the nearest preceding `type` header. Cheaper and more
  // obviously correct than mapping byte offsets to line numbers, and it reads the same way the
  // grammar does: a block starts at its header and runs until the next one.
  for (let i = line - 1; i >= 0; i -= 1) {
    const match = /^type\s+(\S+)/.exec(lines[i] ?? '');
    if (match !== null) {
      return match[1] ?? null;
    }
  }
  return null;
}

export interface DocumentLine {
  /** 1-indexed, matching the wire's diagnostic `line` field. */
  number: number;
  text: string;
  offending: boolean;
}

/** Splits `document` into 1-indexed lines, marking `offendingLine` (if given) — the degraded
 * whole-document view's "offending location marked" (`client-colony`). */
export function documentLines(document: string, offendingLine?: number): DocumentLine[] {
  return document.split('\n').map((text, i) => ({ number: i + 1, text, offending: offendingLine === i + 1 }));
}
